import { Component, computed, inject, ElementRef, viewChild, effect, HostBinding, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatService, ChatMessage, FeedbackMetricsSnapshot, ChatMode, ChatPluginCapability } from '../../core/services/chat.service';
import { GuideService } from '../../core/services/guide.service';
import { SearchService, SearchResult } from '../../core/services/search.service';
import { ButtonModule } from '@openng/optimus-ui/button';
import { Message } from '@openng/optimus-ui/message';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { Popover } from '@openng/optimus-ui/popover';
import { ListboxModule } from '@openng/optimus-ui/listbox';
import { SelectModule } from '@openng/optimus-ui/select';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { MarkdownPipe } from '../../shared/pipes/markdown.pipe';
import { debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-chat-sidebar',
  standalone: true,
  imports: [Message, CommonModule, FormsModule, ButtonModule, TextareaModule, PopoverModule, ListboxModule, SelectModule, CheckboxModule, MarkdownPipe, TranslatePipe],
  templateUrl: './chat-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      z-index: 1000;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      width: 0;
      transition: width 0.3s ease-in-out;
      overflow: hidden;
      background-color: var(--surface-card, #ffffff);
      border-left: 1px solid var(--surface-border, var(--stela-line));
      box-shadow: -2px 0 8px rgba(0,0,0,0.1);
    }
    
    :host.open {
      width: min(400px, 90vw);
    }

    ::ng-deep .markdown-content p {
      margin-bottom: 0.5em;
    }
    ::ng-deep .markdown-content p:last-child {
      margin-bottom: 0;
    }
    ::ng-deep .markdown-content ul {
      list-style-type: disc;
      padding-left: 1.5em;
      margin-bottom: 0.5em;
    }
    ::ng-deep .markdown-content ol {
      list-style-type: decimal;
      padding-left: 1.5em;
      margin-bottom: 0.5em;
    }
    ::ng-deep .markdown-content li {
      margin-bottom: 0.25em;
    }
    ::ng-deep .markdown-content pre {
      background-color: rgba(0, 0, 0, 0.1);
      padding: 0.5em;
      border-radius: 0.25em;
      margin-bottom: 0.5em;
      overflow-x: auto;
    }
    ::ng-deep .markdown-content code {
      font-family: monospace;
      background-color: rgba(0, 0, 0, 0.1);
      padding: 0.1em 0.3em;
      border-radius: 0.25em;
    }
    ::ng-deep .markdown-content pre code {
      background-color: transparent;
      padding: 0;
    }
    ::ng-deep .markdown-content a {
      text-decoration: underline;
    }
    ::ng-deep .markdown-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 0.5em 0;
      font-size: 0.85em;
    }
    ::ng-deep .markdown-content th,
    ::ng-deep .markdown-content td {
      border: 1px solid var(--surface-border, var(--stela-line));
      padding: 0.4em 0.6em;
      text-align: left;
    }
    ::ng-deep .markdown-content th {
      background-color: var(--surface-100, var(--stela-bg-muted));
      font-weight: 600;
    }
  `]
})
export class ChatSidebarComponent {
  chatService = inject(ChatService);
  guideService = inject(GuideService);
  searchService = inject(SearchService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  prompt = '';
  chatMode = signal<ChatMode>('query');
  /**
   * Último modo elegido explícitamente por el usuario. Sigue vivo aunque el modo desaparezca
   * temporalmente de `chatModes()` (p. ej. el anclaje de Diseño se suelta y se vuelve a pedir en
   * cada turno, ver `ChatService.availableModes`), para poder restaurarlo cuando reaparezca en
   * vez de dejar al usuario reseleccionándolo en cada mensaje.
   */
  private desiredMode: ChatMode = 'query';
  view = signal<'chat' | 'list' | 'metrics'>('chat');
  canViewMetrics = this.authService.permissionExist('AI_CHAT_METRICS_READ');
  metricsLoading = signal<boolean>(false);
  metricsError = signal<string | null>(null);
  metrics = signal<FeedbackMetricsSnapshot | null>(null);

  /** La etiqueta del modo "Diseño" se traduce aparte: los demás modos son literales sin tocar. */
  chatModes = computed(() => this.chatService.availableModes().map(mode =>
    mode.value === 'template'
      ? { ...mode, label: this.translateService.instant('chat.mode_template') }
      : mode
  ));

  private readonly defaultSuggestedPrompts: string[] = [
    '¿Qué eventos tengo esta semana?',
    '¿Cuántos invitados confirmaron para el próximo evento?',
    '¿Hay tareas pendientes?',
    'Muestra el calendario del mes',
    '¿Qué mailings se han enviado recientemente?',
    'Lista los artículos del CMS'
  ];

  private readonly templateSuggestedPrompts: string[] = [
    'Haz la cabecera más sobria',
    'Pon la fecha y el lugar reales del evento',
    'Revisa que se lea bien en móvil',
    'Busca una imagen de portada para una gala'
  ];

  /** Los prompts sugeridos son del modo activo: en "Diseño" no tiene sentido ofrecer los de eventos. */
  suggestedPrompts = computed(() =>
    this.chatMode() === 'template' ? this.templateSuggestedPrompts : this.defaultSuggestedPrompts
  );

  /** Una sesión que edita algo tiene que decir qué edita; si no, es una trampa para el usuario. */
  anchorLabel = computed(() => {
    const context = this.chatService.activeSession()?.context;
    return context?.kind === 'mailing-template' ? context : null;
  });

  private readonly scrollContainer = viewChild<ElementRef>('scrollContainer');
  readonly mentionOp = viewChild<Popover>('mentionOp');
  readonly toolsOp = viewChild<Popover>('toolsOp');
  private readonly textarea = viewChild<ElementRef>('textarea');

  mentionQuery = signal<string>('');
  mentionTrigger = signal<string>('');
  mentionResults = signal<SearchResult[]>([]);
  
  constructor() {
    effect(() => {
      // Auto-scroll to bottom when messages change and we are in chat view
      const msgs = this.chatService.messages();
      if (this.view() === 'chat') {
        setTimeout(() => this.scrollToBottom(), 100);
      }
    });

    effect(() => {
      const available = this.chatModes().map(mode => mode.value);
      if (available.includes(this.desiredMode)) {
        // El modo que el usuario quería está disponible de nuevo (p. ej. se reanclo la sesión
        // de Diseño): restaurarlo en vez de dejarlo en el fallback temporal.
        if (this.chatMode() !== this.desiredMode) {
          this.chatMode.set(this.desiredMode);
        }
        return;
      }
      if (!available.includes(this.chatMode())) {
        this.chatMode.set('query');
      }
    });

    toObservable(this.mentionQuery)
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => {
            if (!term || term.length < 2) return of([]);
            return this.searchService.search(term);
        })
      )
      .subscribe(results => {
          if (this.mentionTrigger() === '@') {
              this.mentionResults.set(results.filter(r => r.entity === 'Person' || r.entity === 'Persona'));
          } else {
              this.mentionResults.set(results);
          }
      });
  }

  @HostBinding('class.open')
  get isOpen() {
    return this.chatService.visible();
  }

  close() {
    this.chatService.toggle();
  }

  /** Selección explícita del usuario en el desplegable de modo: se recuerda para poder restaurarla. */
  onModeSelect(mode: ChatMode) {
    this.desiredMode = mode;
    this.chatMode.set(mode);
  }

  toggleView() {
    this.view.update(v => v === 'chat' ? 'list' : 'chat');
  }

  startNewChat() {
    this.chatService.startNewSession();
    this.view.set('chat');
    setTimeout(() => this.scrollToBottom(), 100);
  }

  selectSession(sessionId: string) {
    this.chatService.selectSession(sessionId);
    this.view.set('chat');
    setTimeout(() => this.scrollToBottom(), 100);
  }

  deleteSession(event: Event, sessionId: string) {
    event.stopPropagation();
    this.chatService.deleteSession(sessionId);
  }

  sendMessage() {
    if (!this.prompt.trim()) return;
    this.chatService.sendMessage(this.prompt, this.chatMode());
    this.prompt = '';
  }

  handleKeydown(event: Event) {
    const keyboardEvent = event as KeyboardEvent;

    if (this.mentionOp() && this.mentionOp()!.overlayVisible) {
        // Let listbox handle navigation if overlay is visible
        return;
    }

    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  handleInput(event: any) {
    const value = event.target.value;
    const cursor = event.target.selectionStart;
    const lastChar = value.slice(cursor - 1, cursor);
    
    if (lastChar === '@' || lastChar === '#') {
        this.mentionTrigger.set(lastChar);
        this.mentionQuery.set('');
        this.mentionOp()?.toggle(event, event.target);
    } else if (this.mentionOp() && this.mentionOp()!.overlayVisible) {
        // If overlay is open, update query based on text after last @/#
        const lastAt = value.lastIndexOf('@', cursor);
        const lastHash = value.lastIndexOf('#', cursor);
        const triggerIndex = Math.max(lastAt, lastHash);

        if (triggerIndex !== -1) {
            this.mentionTrigger.set(value.charAt(triggerIndex));
            this.mentionQuery.set(value.slice(triggerIndex + 1, cursor));
        } else {
            this.mentionOp()?.hide();
        }
    }
  }

  selectEntity(entity: SearchResult) {
    const cursor = this.textarea()?.nativeElement.selectionStart;
    const value = this.prompt;
    const lastAt = value.lastIndexOf('@', cursor);
    const lastHash = value.lastIndexOf('#', cursor);
    const triggerIndex = Math.max(lastAt, lastHash);

    if (triggerIndex !== -1) {
        const prefix = value.slice(0, triggerIndex);
        const suffix = value.slice(cursor);
        this.prompt = `${prefix}[${entity.entity}: ${entity.display}]${suffix}`; // Insert formatted entity
        this.mentionOp()?.hide();

        // Restore focus
        setTimeout(() => this.textarea()?.nativeElement.focus(), 0);
    }
  }

  triggerMention(event: Event) {
      this.prompt += '@';
      this.mentionQuery.set('');
      // Delay to allow ngModel to update
      setTimeout(() => {
        this.mentionOp()?.show(event, this.textarea()?.nativeElement);
        this.textarea()?.nativeElement.focus();
      }, 0);
  }

  canConfigureTools(): boolean {
    return this.canUseToolsForMode(this.chatMode()) && this.chatService.availablePlugins().length > 0;
  }

  availableToolPlugins(): ChatPluginCapability[] {
    return this.chatService.availablePlugins();
  }

  toggleToolPlugin(pluginName: string, enabled: boolean): void {
    const sessionId = this.chatService.activeSessionId();
    if (!sessionId) {
      return;
    }

    this.chatService.togglePlugin(sessionId, pluginName, enabled);
  }

  isToolPluginEnabled(pluginName: string): boolean {
    const sessionId = this.chatService.activeSessionId();
    if (!sessionId) {
      return true;
    }

    return this.chatService.isPluginEnabled(sessionId, pluginName);
  }

  private canUseToolsForMode(mode: ChatMode): boolean {
    return mode === 'query' || mode === 'plan';
  }

  openMetrics(): void {
    if (!this.canViewMetrics) {
      return;
    }

    this.view.set('metrics');
    if (!this.metrics()) {
      void this.loadMetrics();
    }
  }

  backFromMetrics(): void {
    this.view.set('list');
  }

  async loadMetrics(): Promise<void> {
    if (!this.canViewMetrics) {
      return;
    }

    this.metricsLoading.set(true);
    this.metricsError.set(null);
    try {
      const snapshot = await this.chatService.getFeedbackMetrics(undefined, false);
      this.metrics.set(snapshot);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'No se pudieron cargar las métricas.';
      this.metricsError.set(message);
    } finally {
      this.metricsLoading.set(false);
    }
  }

  lastAssistantMessage = computed(() => {
    const msgs = this.chatService.messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') return msgs[i];
    }
    return null;
  });

  useSuggestedPrompt(prompt: string) {
    this.prompt = prompt;
    this.sendMessage();
  }

  copyMessage(msg: { content: string }) {
    navigator.clipboard.writeText(msg.content);
  }

  retryLastMessage() {
    const msgs = this.chatService.messages();
    const lastUser = [...msgs].reverse().find(m => m.role === 'user');
    if (lastUser) {
      this.chatService.sendMessage(lastUser.content, this.chatMode());
    }
  }

  rateMessage(messageId: string, feedback: 'like' | 'dislike') {
    const sessionId = this.chatService.activeSessionId();
    if (sessionId) {
      this.chatService.setFeedback(sessionId, messageId, feedback);
    }
  }

  handleMessageClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a');
    if (!anchor) {
      return;
    }

    const rawHref = anchor.getAttribute('href');
    if (!rawHref || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) {
      return;
    }

    try {
      const destination = new URL(rawHref, window.location.origin);
      if (destination.origin !== window.location.origin) {
        return;
      }

      event.preventDefault();
      const appUrl = this.normalizeLegacyRoute(destination);
      this.router.navigateByUrl(appUrl);
    } catch {
      // Ignore malformed URLs and let browser handle them.
    }
  }

  approveAction(msg: ChatMessage) {
    const sessionId = this.chatService.activeSessionId();
    if (sessionId && msg.pendingAction) {
      void this.chatService.confirmAction(sessionId, msg.id, msg.pendingAction.actionId);
    }
  }

  denyAction(msg: ChatMessage) {
    const sessionId = this.chatService.activeSessionId();
    if (sessionId) {
      this.chatService.denyAction(sessionId, msg.id);
    }
  }

  toggleAlwaysAllow(actionType: string, enabled: boolean) {
    const sessionId = this.chatService.activeSessionId();
    if (sessionId) {
      this.chatService.toggleAutoApprove(sessionId, actionType, enabled);
    }
  }

  private normalizeLegacyRoute(destination: URL): string {
    const hash = destination.hash || '';

    if (hash.startsWith('#/')) {
      return hash.slice(1);
    }

    if (hash.startsWith('#!/')) {
      return hash.replace(/^#!/, '');
    }

    if (hash.length > 0 && hash.startsWith('#')) {
      return hash.slice(1);
    }

    return `${destination.pathname}${destination.search}${destination.hash}`;
  }

  hasSteps(content: string): boolean {
    if (!content) return false;
    const lines = content.split('\n');
    let listItems = 0;
    for (const line of lines) {
        if (/^(\d+\.|-|\*)\s/.test(line.trim())) {
            listItems++;
        }
    }
    return listItems >= 2;
  }

  startGuide(content: string) {
    const lines = content.split('\n');
    const steps: string[] = [];
    for (const line of lines) {
        const trimmed = line.trim();
        if (/^(\d+\.|-|\*)\s/.test(trimmed)) {
            // Remove the bullet/number and any markdown bold markers for cleaner text in the overlay
            let stepContent = trimmed.replace(/^(\d+\.|-|\*)\s/, '').trim();
            steps.push(stepContent);
        }
    }
    
    if (steps.length > 0) {
        this.guideService.startGuide(steps);
        this.close(); // Close sidebar to reveal the app
    }
  }

  private scrollToBottom(): void {
    try {
      const container = this.scrollContainer();
      if (container) {
        container.nativeElement.scrollTop = container.nativeElement.scrollHeight;
      }
    } catch (err) { }
  }
}
